<template>
  <v-container fluid>
    <v-breadcrumbs :items="breadItems"
                   class="breadcrumb mx-16">
    </v-breadcrumbs>

    <v-row class="mx-13">
      <v-col cols="12"
             sm="12"
             lg="4"
             md="4">
        <v-card class="mb-2">
          <v-card-title class="justify-center font-weight-bold grey lighten-2">
            <span>Splicing Regulatory Network</span>
          </v-card-title>
          <div class="d-flex flex-no-wrap justify-space-between">
            <p class="text-left text-body-1 ma-4">In this section, you can interactively view the splicing regulators and
              corresponding target genes, which were all curated from published literatures.
            </p>
            <v-avatar class="ma-3"
                      size="120"
                      rounded
                      right
                      color="">
              <v-img src="../assets/img/tools/regulation_network.svg"></v-img>
            </v-avatar>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn color="rgb(245, 177, 73)"
                       v-bind="attrs"
                       class="mx-2"
                       v-on="on"
                       to="/tools/regnet">
                  <span class="font-weight-bold">Go !</span>
                </v-btn>
              </template>
              <span>View the most complete mis-splicing regulatory network in human cancers.</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12"
             lg="4"
             md="4"
             sm="12">
        <v-card>
          <v-card-title class="justify-center font-weight-bold grey lighten-2">
            Survival Analysis
          </v-card-title>
          <div class="d-flex flex-no-wrap justify-space-between">
            <p class="text-left text-body-1 ma-4">In this section, you can perform survival analysis
              to estimate the prognostic significance of splicing events for TCGA cancer types.</p>
            <v-avatar class="ma-3"
                      size="120"
                      rounded
                      right>
              <v-img src="../assets/img/tools/survival_plot.svg"></v-img>
            </v-avatar>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn color="rgb(114, 187, 232)"
                       v-bind="attrs"
                       class="mx-2"
                       v-on="on"
                       to="/tools/survival/asevent">
                  <span class="font-weight-bold">Go !</span>
                </v-btn>
              </template>
              <span>Estimate the prognostic significance of splicing events.</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12"
             lg="4"
             md="4"
             sm="12">
        <v-card class="mb-2">
          <v-card-title class="justify-center font-weight-bold grey lighten-2">
            Splicing-derived Drug Response
          </v-card-title>
          <div class="d-flex flex-no-wrap justify-space-between">
            <p class="text-left text-body-1 ma-4">In this section, you can explore the impact of alternative splicing
              on drug-gene interactions at the isoform sequence and protein structure levels.</p>
            <v-avatar class="ma-3"
                      size="120"
                      rounded
                      right
                      color="">
              <v-img src="../assets/img/tools/drug.svg"></v-img>
            </v-avatar>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn color="rgb(165, 212, 172)"
                       v-bind="attrs"
                       class="mx-2"
                       v-on="on"
                       to="/tools/splicedrug">
                  <span class="font-weight-bold">Go !</span>
                </v-btn>
              </template>
              <span>Explore the impact of alternative splicing on drug-gene interactions.</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12"
             lg="4"
             md="4"
             sm="12">
        <v-card class="mb-2">
          <v-card-title class="justify-center font-weight-bold grey lighten-2">
            Splicing Regulatory Explorer
          </v-card-title>
          <div class="d-flex flex-no-wrap justify-space-between">
            <p class="text-left text-body-1 ma-4">In this section, you can explore the potential regulatory factors
              based on multi-omics evidence of TCGA.</p>
            <v-avatar class="ma-3"
                      size="120"
                      rounded
                      right
                      color="">
              <v-img src="../assets/img/tools/oncoprint.svg"></v-img>
            </v-avatar>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn color="rgb(245, 177, 73)"
                       v-bind="attrs"
                       class="mx-2"
                       v-on="on"
                       to="/tools/regexp?genename=EZH2&source=TCGA&project=TCGA-LAML">
                  <span class="font-weight-bold">Go !</span>
                </v-btn>
              </template>
              <span>Explore the potential splicing regulatory factors based on TCGA multi-omics data.</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12"
             sm="12"
             lg="4"
             md="4">
        <v-card>
          <v-card-title class="justify-center font-weight-bold grey lighten-2">
            Cancer Staging Analysis
          </v-card-title>
          <div class="d-flex flex-no-wrap justify-space-between">
            <p class="text-left text-body-1 ma-4">In this section, you can identify the
              splicing differences between tumor pathological stages for TCGA cancer types.</p>
            <v-avatar class="ma-3"
                      size="120"
                      rounded
                      right>
              <v-img src="../assets/img/tools/cancer_stage_plot.svg"></v-img>
            </v-avatar>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn color="rgb(114, 187, 232)"
                       v-bind="attrs"
                       class="mx-2"
                       v-on="on"
                       to="/tools/stage/asevent">
                  <span class="font-weight-bold">Go !</span>
                </v-btn>
              </template>
              <span>Identify the splicing differences between tumor pathological stages.</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12"
             lg="4"
             md="4"
             sm="12">
        <v-card>
          <v-card-title class="justify-center font-weight-bold grey lighten-2">
            <!-- Splicing Interaction Analysis -->
            Isoform-specific PPIs Network
          </v-card-title>
          <div class="d-flex flex-no-wrap justify-space-between">
            <p class="text-left text-body-1 ma-4">In this section, you can explore the functional role of alternative splicing in protein-protein interactions (PPIs) at the isoform level.</p>
            <v-avatar class="ma-3"
                      size="120"
                      rounded
                      right
                      color="">
              <v-img src="../assets/img/tools/isoform_specific_ppi.svg"></v-img>
            </v-avatar>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn color="rgb(165, 212, 172)"
                       v-bind="attrs"
                       class="mx-2"
                       v-on="on"
                       to="/tools/spliceinter">
                  <span class="font-weight-bold">Go !</span>
                </v-btn>
              </template>
              <span>Explore the effect of alternative splicing on protein-protein interactions at the isoform level.</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'Tools',
  data() {
    return {
      breadItems: [
        {
          text: 'Home',
          disabled: false,
          href: '/ascancer/home',
        },
        {
          text: 'Splicing Toolkit',
          disabled: true,
        },
      ],
    }
  },
}
</script>

<style scoped lang="sass">
.v-card__title
  line-height: 0.5rem

.v-btn
  text-transform: none
  letter-spacing: 0em
  font-size: 0.875rem

.v-sheet.v-card
  // border-radius: 15px

.breadcrumb
  line-height: 1
  display: inline-block
  margin: 0 0
  vertical-align: middle
  padding: 8px 15px
  margin-top: -5px
  margin-bottom: 10px
  list-style: none
  background-color: #f5f5f5
  border-radius: 4px
</style>